import { useState, useEffect, useCallback } from 'react';

interface NetworkStatus {
  isOnline: boolean;
  isChecking: boolean;
  lastOnlineTime: Date | null;
  checkCount: number;
}

export function useNetworkStatus() {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    isChecking: false,
    lastOnlineTime: navigator.onLine ? new Date() : null,
    checkCount: 0,
  });

  const checkConnection = useCallback(async (isManual = false) => {
    if (isManual) {
      setNetworkStatus((prev) => ({ ...prev, isChecking: true }));
    }

    try {
      // Use multiple methods to verify connectivity
      const onlineCheck = navigator.onLine;

      if (onlineCheck) {
        // Additional verification with a simple fetch to a reliable endpoint
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        try {
          await fetch('https://www.google.com/favicon.ico', {
            method: 'HEAD',
            mode: 'no-cors',
            signal: controller.signal,
          });
          clearTimeout(timeoutId);

          setNetworkStatus((prev) => ({
            ...prev,
            isOnline: true,
            lastOnlineTime: new Date(),
            checkCount: prev.checkCount + 1,
            isChecking: false,
          }));

          return true;
        } catch {
          clearTimeout(timeoutId);
          // Fall through to offline handling
        }
      }

      setNetworkStatus((prev) => ({
        ...prev,
        isOnline: false,
        checkCount: prev.checkCount + 1,
        isChecking: false,
      }));

      return false;
    } catch (error) {
      setNetworkStatus((prev) => ({
        ...prev,
        isOnline: false,
        checkCount: prev.checkCount + 1,
        isChecking: false,
      }));

      return false;
    }
  }, []);

  const handleOnline = useCallback(() => {
    setNetworkStatus((prev) => ({
      ...prev,
      isOnline: true,
      lastOnlineTime: new Date(),
    }));
  }, []);

  const handleOffline = useCallback(() => {
    setNetworkStatus((prev) => ({
      ...prev,
      isOnline: false,
    }));
  }, []);

  useEffect(() => {
    // Add event listeners for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial connection check
    checkConnection();

    // Set up periodic checking when offline
    let intervalId: NodeJS.Timeout;

    if (!networkStatus.isOnline) {
      intervalId = setInterval(() => {
        checkConnection();
      }, 10000); // Check every 10 seconds when offline
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [checkConnection, handleOnline, handleOffline, networkStatus.isOnline]);

  return {
    ...networkStatus,
    checkConnection: () => checkConnection(true),
  };
}
